<template>
    <section class="index-blog">
        <Container width="landing">
            <div class="index-blog__header">
                <h2 ref="titleRef"
                    class="index-blog__title"
                >
                    Блог
                </h2>

                <UiSliderNavigation ref="sliderNavigationRef"
                                    class="index-blog__slider-navigation"
                                    :is-button-prev-disabled
                                    :is-button-next-disabled
                />
            </div>
        </Container>
        <div ref="swiperRef"
             class="index-blog__list swiper"
             :class="{ _inited: isSwiperInited }"
        >
            <div class="swiper-wrapper">
                <ArticleCard v-for="article in articles"
                             :key="article.id"
                             class="swiper-slide index-blog__article"
                             :article="article"
                />
            </div>
            <div class="index-blog__pagination" />
        </div>
    </section>
</template>

<script setup lang="ts">
    import type { SwiperOptions } from 'swiper/types'
    import type { ArticleItem } from '~/src/shared/components/pages/index/mock/indexBlog'
    import type UiSliderNavigation from '~/src/shared/components/ui/slider/UiSliderNavigation.vue'
    import { Navigation, Pagination } from 'swiper/modules'
    import ArticleCard from '~/src/entities/article-card/ArticleCard.vue'
    import Container from '~/src/shared/components/common/Container/Container.vue'
    import { useSwiper } from '~/src/shared/composables/client/slider/useSwiper'

    defineProps<{
        articles: ArticleItem[]
    }>()

    const titleRef = ref<HTMLElement>()
    const swiperRef = ref<HTMLElement>()
    const sliderNavigationRef = ref<InstanceType<typeof UiSliderNavigation>>()
    const swiperOptions = computed<SwiperOptions>(() => {
        const titleRect = titleRef.value?.getBoundingClientRect()

        return {
            modules: [Navigation, Pagination],
            slidesPerView: 1,
            spaceBetween: 12,
            navigation: {
                prevEl: sliderNavigationRef.value?.prevEl,
                nextEl: sliderNavigationRef.value?.nextEl,
            },
            pagination: {
                el: '.index-blog__pagination',
                clickable: true,
                type: 'bullets',
            },

            breakpoints: {
                501: {
                    slidesPerView: 'auto',
                    slidesOffsetBefore: titleRect?.left,
                    slidesOffsetAfter: titleRect?.left,
                },
            },
        }
    })

    const { isSwiperInited, isButtonPrevDisabled, isButtonNextDisabled } = useSwiper(swiperRef, swiperOptions)
</script>

<style scoped lang="scss">
    .index-blog {
        padding: 100px 0 0;

        @include respond-to(mobile) {
            padding: 60px 0 40px;
        }

        &__header {
            display: flex;
            align-items: center;
        }

        &__title {
            @include mulish40b;
        }

        &__slider-navigation {
            margin-left: auto;
        }

        &__list {
            position: relative;
            padding: 60px 0 90px;
            opacity: 0;
            transition: opacity 0.2s ease;

            &._inited {
                opacity: 1;
            }

            @include respond-to(mobile) {
                margin-top: 24px;
                padding: 0 12px;
            }

            &:after {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                z-index: 2;
                width: 237px;
                height: 100%;
                background: linear-gradient(90deg, rgb(251 250 255 / 0%) 0%, rgb(233 237 242 / 50%) 100%);
                pointer-events: none;

                @include respond-to(mobile) {
                    display: none;
                }
            }
        }

        &__pagination {
            display: none;

            @include respond-to(mobile) {
                display: block;
                margin-top: 12px;
                text-align: center;
            }

            :deep(.swiper-pagination-bullet) {
                display: inline-block;
                width: 10px;
                height: 10px;
                margin: 0 5px;
                border-radius: 50%;
                background-color: var(--blue-100);
            }

            :deep(.swiper-pagination-bullet-active) {
                background-color: var(--blue-600);
            }
        }

        &__article {
            width: 450px;
        }
    }
</style>
